import React from 'react';
import { message } from 'antd';
import { useForm } from '@refinedev/antd';
import { CommonForm } from './CommonForm';

interface EditFormProps {
  resource: string;
  fields: any[];
  title?: string;
  submitText?: string;
  initialValues?: Record<string, any>;
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (data: any) => void;
  drawerWidth?: number;
  modalWidth?: number;
  formDisplayMode?: 'drawer' | 'modal' | 'inline';
  rowKey?: string;
  // Chat configuration
  enableChat?: boolean;
  chatType?: string;
  chatRelativeId?: number;
  chatPatientId?: number;
  chatTitle?: string;
  chatPlaceholder?: string;
  // Custom title component
  customTitle?: React.ReactNode;
  // Auto-scroll configuration
  isintialScroll?: boolean;
  // Delete configuration
  showDelete?: boolean;
  onDeleteSuccess?: () => void;
}

export const EditForm: React.FC<EditFormProps> = ({
  resource,
  fields,
  title = 'Edit',
  submitText = 'Save Changes',
  initialValues = {},
  open = false,
  onClose,
  onSuccess,
  drawerWidth,
  modalWidth,
  formDisplayMode = 'drawer',
  rowKey = 'id',
  enableChat,
  chatType,
  chatRelativeId,
  chatPatientId,
  chatTitle,
  chatPlaceholder,
  customTitle,
  isintialScroll,
  showDelete = false,
  onDeleteSuccess,
}) => {
  // Use useForm without fetching data since we have initialValues
  const formHookResult = useForm({
    resource,
    id: initialValues?.[rowKey],
    action: 'edit',
    redirect: false,
    queryOptions: {
      enabled: false,
    },
    onMutationSuccess: data => {
      message.success('Record updated successfully!');
      onClose?.();
      onSuccess?.(data);
    },
    onMutationError: error => {
      message.error(
        error?.response?.data?.message ||
          'Failed to update record. Please check your input and try again.'
      );
    },
  });

  const {
    form,
    formProps: editFormProps,
    saveButtonProps: editSaveButtonProps,
    formLoading,
  } = formHookResult;

  // Set form values when initialValues are available and form is ready
  React.useEffect(() => {
    if (initialValues && form && open && !formLoading) {
      // Use setTimeout to ensure form is fully initialized
      setTimeout(() => {
        form?.setFieldsValue(initialValues);
      }, 100);
    }
  }, [initialValues, form, open, formLoading]);

  // Early return if form should not be rendered
  if (!open || !form) {
    return null;
  }

  return (
    <CommonForm
      fields={fields}
      title={title}
      submitText={submitText}
      initialValues={initialValues}
      formDisplayMode={formDisplayMode}
      open={open}
      drawerWidth={drawerWidth}
      modalWidth={modalWidth}
      form={form}
      formProps={editFormProps}
      saveButtonProps={editSaveButtonProps}
      formLoading={formLoading}
      onClose={onClose || (() => {})}
      onFinish={editFormProps.onFinish}
      resourceName={resource}
      loadingText="Loading form..."
      enableChat={enableChat}
      chatType={chatType}
      chatRelativeId={chatRelativeId}
      chatPatientId={chatPatientId}
      chatTitle={chatTitle}
      chatPlaceholder={chatPlaceholder}
      customTitle={customTitle}
      isintialScroll={isintialScroll}
      showDelete={showDelete}
      deleteResource={resource}
      deleteId={initialValues?.[rowKey]}
      onDeleteSuccess={onDeleteSuccess}
      rowKey={rowKey}
    />
  );
};
